import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";

import {
	Badge,
	Button,
	Checkbox,
	Chip,
	FormControlLabel,
	IconButton,
	makeStyles,
	MenuItem,
	Paper,
	Switch,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@material-ui/core";
import { OpenInNew } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import ResponsiveTable from "../../components/ResponsiveTable";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { formatDuration } from "../Dashboard/format";
import BulkActionDialog from "./BulkActionDialog";

const REFRESH_MS = 30000;
const MAX_BULK = 200;
const ACTIONS = ["transfer", "unassign", "close", "markRead"];
const IDLE_OPTIONS = [30, 60, 240, 1440];

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	summary: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	hint: {
		margin: theme.spacing(0, 0, 1.5),
		color: theme.palette.text.secondary,
	},
	filters: {
		display: "flex",
		flexWrap: "wrap",
		alignItems: "center",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1.5),
	},
	field: {
		flex: "1 1 150px",
		minWidth: 130,
	},
	search: {
		flex: "2 1 220px",
	},
	toolbar: {
		position: "sticky",
		top: 0,
		zIndex: 2,
		display: "flex",
		flexWrap: "wrap",
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(1, 1.5),
		marginBottom: theme.spacing(1),
		borderRadius: theme.shape.borderRadius,
		backgroundColor: theme.palette.tintedBackground || theme.palette.action.hover,
		border: `1px solid ${theme.palette.divider}`,
	},
	grow: {
		flex: 1,
	},
	message: {
		maxWidth: 260,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		[theme.breakpoints.down("xs")]: {
			whiteSpace: "normal",
			maxWidth: "none",
		},
	},
	muted: {
		display: "block",
		color: theme.palette.text.secondary,
	},
	queueDot: {
		display: "inline-block",
		width: 8,
		height: 8,
		borderRadius: "50%",
		marginRight: theme.spacing(0.75),
	},
	empty: {
		padding: theme.spacing(4),
		textAlign: "center",
		color: theme.palette.text.secondary,
	},
}));

const Supervisor = () => {
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const { whatsApps } = useContext(WhatsAppsContext);
	const isAdmin = user.profile === "admin";

	const [status, setStatus] = useState("");
	const [queueId, setQueueId] = useState("");
	const [userId, setUserId] = useState("");
	const [whatsappId, setWhatsappId] = useState("");
	const [idle, setIdle] = useState("");
	const [unread, setUnread] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(25);

	const [data, setData] = useState({ items: [], total: 0, summary: { open: 0, pending: 0 } });
	const [loaded, setLoaded] = useState(false);
	const [queues, setQueues] = useState([]);
	const [users, setUsers] = useState([]);
	const [selected, setSelected] = useState({}); // id -> true
	const [action, setAction] = useState(null);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => {
			setSearch(searchInput.trim());
			setPage(0);
		}, 400);
		return () => clearTimeout(timer);
	}, [searchInput]);

	useEffect(() => {
		if (!isAdmin) return;
		Promise.all([api.get("/queue"), api.get("/users/", { params: { pageNumber: 1 } })])
			.then(([q, u]) => {
				setQueues(q.data || []);
				setUsers(u.data.users || []);
			})
			.catch(toastError);
	}, [isAdmin]);

	const load = useCallback(async () => {
		if (!isAdmin) return;
		try {
			const { data: result } = await api.get("/supervisor/tickets", {
				params: {
					status: status || undefined,
					queueId: queueId || undefined,
					userId: userId || undefined,
					whatsappId: whatsappId || undefined,
					idleMinutes: idle || undefined,
					unread: unread ? "true" : undefined,
					search: search || undefined,
					page: page + 1,
					pageSize,
				},
			});
			setData(result);
		} catch (err) {
			toastError(err);
		} finally {
			setLoaded(true);
		}
	}, [isAdmin, status, queueId, userId, whatsappId, idle, unread, search, page, pageSize]);

	useEffect(() => {
		load();
		const timer = setInterval(load, REFRESH_MS);
		return () => clearInterval(timer);
	}, [load]);

	const onFilter = setter => e => {
		setter(e.target.value);
		setPage(0);
	};

	const selectedIds = useMemo(
		() => Object.keys(selected).map(Number),
		[selected]
	);
	const pageIds = data.items.map(i => i.id);
	const allOnPage = pageIds.length > 0 && pageIds.every(id => selected[id]);
	const someOnPage = pageIds.some(id => selected[id]);

	const toggle = id =>
		setSelected(prev => {
			const next = { ...prev };
			if (next[id]) {
				delete next[id];
			} else if (Object.keys(next).length >= MAX_BULK) {
				toast.warn(i18n.t("supervisor.limit", { max: MAX_BULK }));
			} else {
				next[id] = true;
			}
			return next;
		});

	const togglePage = () =>
		setSelected(prev => {
			const next = { ...prev };
			if (allOnPage) {
				pageIds.forEach(id => delete next[id]);
				return next;
			}
			let limited = false;
			pageIds.forEach(id => {
				if (next[id]) return;
				if (Object.keys(next).length >= MAX_BULK) {
					limited = true;
					return;
				}
				next[id] = true;
			});
			if (limited) toast.warn(i18n.t("supervisor.limit", { max: MAX_BULK }));
			return next;
		});

	const run = async extra => {
		setBusy(true);
		try {
			const { data: result } = await api.post("/supervisor/bulk", {
				action,
				ticketIds: selectedIds,
				...extra,
			});
			const text = i18n.t("supervisor.result", {
				done: result.done,
				skipped: result.skipped.length,
				failed: result.failed.length,
			});
			if (result.failed.length) toast.warn(text);
			else toast.success(text);
			setSelected({});
			setAction(null);
			await load();
		} catch (err) {
			toastError(err);
		}
		setBusy(false);
	};

	if (!isAdmin) return <Redirect to="/" />;

	return (
		<MainContainer>
			<BulkActionDialog
				action={action}
				count={selectedIds.length}
				queues={queues}
				users={users}
				busy={busy}
				onClose={() => setAction(null)}
				onConfirm={run}
			/>

			<MainHeader>
				<Title>{i18n.t("supervisor.title")}</Title>
			</MainHeader>

			<Paper className={classes.mainPaper} variant="outlined">
				<div className={classes.summary}>
					<Chip
						size="small"
						variant="outlined"
						label={`${i18n.t("supervisor.summary.open")}: ${data.summary.open}`}
					/>
					<Chip
						size="small"
						variant="outlined"
						label={`${i18n.t("supervisor.summary.pending")}: ${data.summary.pending}`}
					/>
				</div>
				<Typography variant="body2" className={classes.hint}>
					{i18n.t("supervisor.hint", { max: MAX_BULK })}
				</Typography>

				<div className={classes.filters}>
					<TextField
						className={classes.search}
						size="small"
						variant="outlined"
						label={i18n.t("supervisor.search")}
						value={searchInput}
						onChange={e => setSearchInput(e.target.value)}
					/>
					<TextField
						select
						className={classes.field}
						size="small"
						variant="outlined"
						label={i18n.t("supervisor.filters.status")}
						value={status}
						onChange={onFilter(setStatus)}
					>
						<MenuItem value="">{i18n.t("supervisor.filters.allStatus")}</MenuItem>
						<MenuItem value="open">{i18n.t("supervisor.status.open")}</MenuItem>
						<MenuItem value="pending">{i18n.t("supervisor.status.pending")}</MenuItem>
					</TextField>
					<TextField
						select
						className={classes.field}
						size="small"
						variant="outlined"
						label={i18n.t("supervisor.filters.queue")}
						value={queueId}
						onChange={onFilter(setQueueId)}
					>
						<MenuItem value="">{i18n.t("supervisor.filters.allQueues")}</MenuItem>
						<MenuItem value="none">{i18n.t("supervisor.filters.noQueue")}</MenuItem>
						{queues.map(q => (
							<MenuItem key={q.id} value={q.id}>
								{q.name}
							</MenuItem>
						))}
					</TextField>
					<TextField
						select
						className={classes.field}
						size="small"
						variant="outlined"
						label={i18n.t("supervisor.filters.attendant")}
						value={userId}
						onChange={onFilter(setUserId)}
					>
						<MenuItem value="">{i18n.t("supervisor.filters.allAttendants")}</MenuItem>
						<MenuItem value="none">{i18n.t("supervisor.filters.noAttendant")}</MenuItem>
						{users.map(u => (
							<MenuItem key={u.id} value={u.id}>
								{u.name}
							</MenuItem>
						))}
					</TextField>
					<TextField
						select
						className={classes.field}
						size="small"
						variant="outlined"
						label={i18n.t("supervisor.filters.connection")}
						value={whatsappId}
						onChange={onFilter(setWhatsappId)}
					>
						<MenuItem value="">{i18n.t("supervisor.filters.allConnections")}</MenuItem>
						{whatsApps.map(w => (
							<MenuItem key={w.id} value={w.id}>
								{w.name}
							</MenuItem>
						))}
					</TextField>
					<TextField
						select
						className={classes.field}
						size="small"
						variant="outlined"
						label={i18n.t("supervisor.filters.idle")}
						value={idle}
						onChange={onFilter(setIdle)}
					>
						<MenuItem value="">{i18n.t("supervisor.filters.anyIdle")}</MenuItem>
						{IDLE_OPTIONS.map(m => (
							<MenuItem key={m} value={m}>
								{i18n.t("supervisor.filters.idleAtLeast", { time: formatDuration(m * 60) })}
							</MenuItem>
						))}
					</TextField>
					<FormControlLabel
						control={
							<Switch
								size="small"
								color="primary"
								checked={unread}
								onChange={e => {
									setUnread(e.target.checked);
									setPage(0);
								}}
							/>
						}
						label={i18n.t("supervisor.filters.unread")}
					/>
				</div>

				{selectedIds.length > 0 && (
					<div className={classes.toolbar}>
						<Typography variant="subtitle2">
							{i18n.t("supervisor.selected", { total: selectedIds.length, max: MAX_BULK })}
						</Typography>
						<span className={classes.grow} />
						{ACTIONS.map(a => (
							<Button
								key={a}
								size="small"
								variant="contained"
								color={a === "close" ? "secondary" : "primary"}
								onClick={() => setAction(a)}
							>
								{i18n.t(`supervisor.actions.${a}.button`)}
							</Button>
						))}
						<Button size="small" onClick={() => setSelected({})}>
							{i18n.t("supervisor.clear")}
						</Button>
					</div>
				)}

				{loaded && data.items.length === 0 ? (
					<div className={classes.empty}>
						<Typography variant="body1">{i18n.t("supervisor.empty")}</Typography>
					</div>
				) : (
					<ResponsiveTable>
						<TableHead>
							<TableRow>
								<TableCell padding="checkbox">
									<Checkbox
										size="small"
										checked={allOnPage}
										indeterminate={!allOnPage && someOnPage}
										onChange={togglePage}
										inputProps={{ "aria-label": i18n.t("supervisor.selectPage") }}
									/>
								</TableCell>
								<TableCell>{i18n.t("supervisor.table.contact")}</TableCell>
								<TableCell>{i18n.t("supervisor.table.lastMessage")}</TableCell>
								<TableCell align="center">{i18n.t("supervisor.table.status")}</TableCell>
								<TableCell>{i18n.t("supervisor.table.attendant")}</TableCell>
								<TableCell>{i18n.t("supervisor.table.queue")}</TableCell>
								<TableCell>{i18n.t("supervisor.table.idle")}</TableCell>
								<TableCell align="center">{i18n.t("supervisor.table.actions")}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{data.items.map(item => (
								<TableRow key={item.id} hover selected={!!selected[item.id]}>
									<TableCell padding="checkbox">
										<Checkbox
											size="small"
											checked={!!selected[item.id]}
											onChange={() => toggle(item.id)}
										/>
									</TableCell>
									<TableCell>
										<Badge
											color="secondary"
											badgeContent={item.unreadMessages}
											max={99}
										>
											<span>{item.contactName || "—"}</span>
										</Badge>
										<span className={classes.muted}>{item.contactNumber}</span>
										<span className={classes.muted}>{item.whatsappName}</span>
									</TableCell>
									<TableCell data-label={i18n.t("supervisor.table.lastMessage")}>
										<Tooltip title={item.lastMessage || ""}>
											<span className={classes.message}>{item.lastMessage || "—"}</span>
										</Tooltip>
									</TableCell>
									<TableCell align="center" data-label={i18n.t("supervisor.table.status")}>
										<Chip
											size="small"
											variant={item.status === "open" ? "default" : "outlined"}
											color={item.status === "open" ? "primary" : "default"}
											label={i18n.t(`supervisor.status.${item.status}`)}
										/>
									</TableCell>
									<TableCell data-label={i18n.t("supervisor.table.attendant")}>
										{item.userName || i18n.t("supervisor.nobody")}
									</TableCell>
									<TableCell data-label={i18n.t("supervisor.table.queue")}>
										{item.queueName ? (
											<span>
												<span
													className={classes.queueDot}
													style={{ backgroundColor: item.queueColor || "#999" }}
												/>
												{item.queueName}
											</span>
										) : (
											"—"
										)}
									</TableCell>
									<TableCell data-label={i18n.t("supervisor.table.idle")}>
										<Tooltip title={new Date(item.updatedAt).toLocaleString()}>
											<span>
												{formatDuration(
													Math.max(0, (Date.now() - new Date(item.updatedAt).getTime()) / 1000)
												)}
											</span>
										</Tooltip>
									</TableCell>
									<TableCell align="center">
										<Tooltip title={i18n.t("supervisor.openTicket")}>
											<IconButton
												size="small"
												onClick={() => history.push(`/tickets/${item.id}`)}
											>
												<OpenInNew fontSize="small" />
											</IconButton>
										</Tooltip>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</ResponsiveTable>
				)}

				{data.total > 0 && (
					<TablePagination
						component="div"
						count={data.total}
						page={page}
						rowsPerPage={pageSize}
						rowsPerPageOptions={[25, 50, 100]}
						labelRowsPerPage={i18n.t("supervisor.perPage")}
						onChangePage={(_, next) => setPage(next)}
						onChangeRowsPerPage={e => {
							setPageSize(Number(e.target.value));
							setPage(0);
						}}
						labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
					/>
				)}
			</Paper>
		</MainContainer>
	);
};

export default Supervisor;
